import React from 'react';
import {observer} from 'mobx-react';
import {inject, Stores} from '../../inject';
import {classes, stylesheet} from 'typestyle';
import {List, AutoSizer} from 'react-virtualized';
import {NAV_BAR_HEIGHT} from '../../views/MainView';
import {I18n, TProvider} from '../../i18n/i18n';
import ReportEditor from './ReportEditor';
import ReportListOptionButton from './ReportListOptionButton';
import {observable} from 'mobx';
import Modal from '../../Modal';
import {LoadingHover} from '../../views/Legend/TermItem';
import {Report} from '../types/Report';
import {DownloadReportModalBody} from './DownloadReportModalBody';
import {LightGreyColor, BlueColor, WhiteColor, GreyColor, RedColor, BlackColorFaded05, Shadow1, GreenColor, BlueShadow1} from '../../styles/colors';
import {important, rgba} from 'csx';
import ChooseFromDefaultTemplateModal from './ChooseFromDefaultTemplateModal';

const LIST_ROW_HEIGHT = 60;

@observer
class ReportView extends React.Component<Stores<'store' | 'reportStore' | 'reportEditorStore'>, {}> {
    private listRef?: List;
    @observable private searchText = '';
    private searchTimeout?: number;
    @observable private searchResult: Report[] = [];
    @observable private searchIsFocused = false;

    private rowRenderer = ({key, index, style}) => {
        const reportsAfterSearch = this.searchText !== '' ? this.searchResult : this.props.reportStore.availableReports;
        const report = reportsAfterSearch[index];

        if (!report) {
            return null;
        }

        return (
            <div key={key} className={reportCss.listRow} style={style}>
                <div className={reportCss.reportName}>{report.name}</div>
                <ReportListOptionButton
                    downloadingReportIds={this.props.reportStore.downloadingReportsIds}
                    editReport={this.props.reportEditorStore.editReport}
                    setReportRemovalModal={this.props.reportStore.setReportRemovalModal}
                    report={report}
                    openReportGenerationModal={this.props.reportStore.openReportGenerationModal}
                />
            </div>
        );
    };

    private createNewReport = () => {
        this.props.reportEditorStore.openReportEditor();
    };

    public componentDidUpdate() {
        if (this.listRef) {
            this.listRef.forceUpdateGrid();
        }
    }

    private noRowsRenderer = () => {
        return (
            <div className={reportCss.noReportsAvailable}>
                <I18n i18nKey={'report:noReportsAvailable'} />
            </div>
        );
    };

    private setListRef = (ref: List) => {
        if (ref) {
            this.listRef = ref;
        }
    };

    private runSearch = (e) => {
        const text = e.currentTarget.value;
        this.searchText = text;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = window.setTimeout(() => {
            this.searchResult = this.props.reportStore.availableReports.filter(
                (report) => report.name.toLowerCase().indexOf(text.toLowerCase()) !== -1
            );
        }, 200);
    };

    private reportDeletionModal(): React.ReactNode {
        const {reportStore} = this.props;
        const idReport = reportStore.idReportToRemove;
        const reportToDelete = reportStore.availableReports.find((report) => report.id === idReport);
        if (!idReport || !reportToDelete) {
            return null;
        }

        const removeReport = () => {
            reportStore.removeReport(idReport).then(() => reportStore.setReportRemovalModal());
        };

        return (
            <Modal
                store={this.props.store}
                modalHeader={<I18n i18nKey={'report:removeReport'} />}
                modalBody={
                    <div>
                        <I18n i18nKey={'report:reportRemovalCheck'} values={{name: reportToDelete.name}} />
                    </div>
                }
                modalFooter={
                    <div>
                        <button className={classes(reportCss.deleteButton, 'delete')} onClick={() => removeReport()}>
                            <I18n i18nKey={'yes'} />
                            {this.showReportDeletionSpinner()}
                        </button>
                        <button className={reportCss.deleteButton} onClick={() => reportStore.setReportRemovalModal()}>
                            <I18n i18nKey={'no'} />
                        </button>
                    </div>
                }
                onModalClose={() => reportStore.setReportRemovalModal()}
            />
        );
    }

    private showReportDeletionSpinner() {
        const {reportIsGettingDeleted} = this.props.reportStore;

        if (!reportIsGettingDeleted) {
            return null;
        }

        return <LoadingHover />;
    }

    private renderReportGenerationModal(): React.ReactNode {
        const {generateReportModalIsOpenForReport, downloadingReportsIds} = this.props.reportStore;

        if (generateReportModalIsOpenForReport) {
            const allDateRangesAreFilled =
                generateReportModalIsOpenForReport.dateRangesWithId.length === generateReportModalIsOpenForReport.report.dates.length;

            const currentlyDownloading = downloadingReportsIds.some(
                (downloadingReportId) => downloadingReportId === generateReportModalIsOpenForReport.report.id
            );

            return (
                <Modal
                    store={this.props.store}
                    modalHeader={<I18n i18nKey={'report:downloadReport'} />}
                    modalBody={
                        <DownloadReportModalBody
                            report={generateReportModalIsOpenForReport.report}
                            updateTextVariableValue={this.props.reportStore.updateTextVariableValue}
                            dateRangesWithId={generateReportModalIsOpenForReport.dateRangesWithId}
                            variablesWithValue={generateReportModalIsOpenForReport.textVariablesWithValue}
                            updateDateRangeRegister={this.props.reportStore.updateDateRangeRegisterForReportGenerationModal}
                        />
                    }
                    modalFooter={
                        <button
                            disabled={!allDateRangesAreFilled}
                            onClick={() =>
                                this.props.reportStore.generateReport(
                                    generateReportModalIsOpenForReport.report.id,
                                    generateReportModalIsOpenForReport.dateRangesWithId,
                                    generateReportModalIsOpenForReport.textVariablesWithValue
                                )
                            }
                            className={classes(reportCss.downloadButton, allDateRangesAreFilled ? undefined : 'disabled')}>
                            {currentlyDownloading ? <LoadingHover /> : null}
                            <I18n i18nKey={'report:downloadReport'} />
                        </button>
                    }
                    onModalClose={this.props.reportStore.closeReportGenerationModal}
                />
            );
        }
        return null;
    }

    private renderReportBody(): React.ReactNode {
        const {reportEditing} = this.props.reportEditorStore;
        const availableReports = this.props.reportStore.availableReports;
        const reportsAfterSearch = this.searchText !== '' ? this.searchResult : availableReports;

        if (reportEditing.editorIsActive) {
            return (
                <ReportEditor
                    store={this.props.store}
                    targetCurrency={this.props.store.currencyStore.targetCurrency}
                    getFullChannelForChannelName={this.props.store.channelStore.getFullChannelForChannelName}
                    moneyFormatToTargetCurrency={this.props.store.currencyStore.moneyFormatToTargetCurrency}
                    reportEditorStore={this.props.reportEditorStore}
                />
            );
        }

        return (
            <div className={reportCss.paperContainer}>
                <div className={reportCss.paper}>
                    <div className={classes(reportCss.optionSearchContainer, this.searchIsFocused ? 'focused' : undefined)}>
                        <div className={reportCss.optionBar}>
                            <button className={reportCss.addReportButton} onClick={this.createNewReport}>
                                <I18n i18nKey={'new'} />
                            </button>
                            <button className={reportCss.addReportButton} onClick={() => (this.props.reportStore.defaultReportModalIsOpen = true)}>
                                <I18n i18nKey={'report:fromTemplate'} />
                            </button>
                        </div>
                        <div className={reportCss.searchBar}>
                            <TProvider>
                                {(t) => (
                                    <input
                                        onFocus={() => (this.searchIsFocused = true)}
                                        onBlur={() => (this.searchIsFocused = false)}
                                        value={this.searchText}
                                        onChange={this.runSearch}
                                        className={reportCss.searchInput}
                                        type="text"
                                        placeholder={t('search')}
                                    />
                                )}
                            </TProvider>
                        </div>
                    </div>
                    <div className={reportCss.listContainer}>
                        <AutoSizer>
                            {(p) => {
                                return (
                                    <List
                                        ref={this.setListRef}
                                        width={p.width}
                                        height={p.height}
                                        rowCount={reportsAfterSearch.length}
                                        rowHeight={LIST_ROW_HEIGHT}
                                        rowRenderer={this.rowRenderer}
                                        noRowsRenderer={this.noRowsRenderer}
                                    />
                                );
                            }}
                        </AutoSizer>
                    </div>
                </div>
            </div>
        );
    }

    public render() {
        const {reportStore, reportEditorStore} = this.props.store;
        reportStore.downloadingReportsIds;
        const {windowHeight} = this.props.store;
        return (
            <div className={reportCss.reportView} style={{height: windowHeight - NAV_BAR_HEIGHT}}>
                {this.renderReportBody()}
                {this.renderReportGenerationModal()}
                {this.reportDeletionModal()}
                <ChooseFromDefaultTemplateModal
                    setDefaultReportModalIsOpen={(open) => (reportStore.defaultReportModalIsOpen = open)}
                    saveNewReport={reportStore.saveNewReport}
                    defaultReports={reportStore.defaultReports}
                    editReport={reportEditorStore.editReport}
                    defaultReportModalIsOpen={reportStore.defaultReportModalIsOpen}
                />
            </div>
        );
    }
}

const reportCss = stylesheet({
    deleteButton: {
        cursor: 'pointer',
        position: 'relative',
        backgroundColor: BlueColor,
        width: 70,
        borderRadius: 2,
        border: 'none',
        color: WhiteColor,
        padding: '5px 15px 5px 15px',
        $nest: {
            '&.delete': {
                backgroundColor: RedColor,
                marginRight: 10,
            },
        },
    },
    downloadButton: {
        position: 'relative',
        overflow: 'hidden',
        border: 'none',
        borderRadius: 2,
        padding: '5px 15px 5px 15px',
        color: WhiteColor,
        backgroundColor: BlueColor,
        $nest: {
            '&.disabled': {
                backgroundColor: 'transparent',
                border: '1px solid ' + LightGreyColor,
                color: GreyColor,
            },
        },
    },
    noReportsAvailable: {
        display: 'flex',
        color: GreyColor,
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },
    listContainer: {
        flex: 1,
        flexGrow: 1,
        boxShadow: Shadow1,
    },
    reportName: {
        flex: 1,
        flexGrow: 1,
        textAlign: 'left',
    },
    fromTemplate: {
        backgroundColor: important(GreenColor),
    },
    addReportButton: {
        borderRight: '1px solid #ddd',
        transition: '.2s',
        cursor: 'pointer',
        paddingLeft: 15,
        paddingRight: 15,
        height: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        $nest: {
            '&:hover': {
                background: rgba(0, 0, 0, 0.2).toString(),
            },
        },
    },
    searchInput: {
        outline: 'none',
        flex: 1,
        flexGrow: 1,
        height: '100%',
        border: 'none',
        padding: '0px 15px 0px 15px',
    },
    searchBar: {
        display: 'flex',
        flexDirection: 'row',
        height: LIST_ROW_HEIGHT,
    },
    listRow: {
        paddingLeft: 15,
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid ' + LightGreyColor,
        transition: '0.2s',
        $nest: {
            '&:hover': {
                backgroundColor: BlackColorFaded05,
            },
            '&:last-child': {
                borderBottom: important('none'),
            },
        },
    },
    reportView: {
        position: 'relative',
        backgroundColor: WhiteColor,
    },
    paperContainer: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 25px 25px 25px',
        width: '100%',
        height: '100%',
    },
    paper: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 1000,
        height: '100%',
        width: '100%',
    },
    reportFromTemplateButton: {
        marginTop: 15,
        cursor: 'pointer',
        outline: 'none',
        border: 'none',
        padding: 5,
        borderRadius: 2,
        color: 'white',
        background: GreenColor,
    },
    reportTemplateLine: {
        display: 'flex',
        alignItems: 'center',
        padding: '15px 0px',
        borderBottom: '1px solid #ddd',
        $nest: {
            '&:last-child': {
                borderBottom: important('none'),
            },
        },
    },
    reportTemplateText: {
        flexGrow: 1,
    },
    reportTemplateActions: {
        whiteSpace: 'nowrap',
    },
    reportTemplateActionButton: {
        outline: 'none',
        border: 'none',
        borderRadius: 2,
        padding: 5,
        marginLeft: 5,
    },
    optionBar: {
        display: 'flex',
        opacity: 0.9,
    },
    optionSearchContainer: {
        boxShadow: Shadow1,
        marginBottom: 15,
        $nest: {
            '&.focused': {
                boxShadow: important(BlueShadow1),
            },
        },
    },
});

export default inject('store', 'reportStore', 'reportEditorStore')(ReportView);



// WEBPACK FOOTER //
// ./src/report/views/ReportView.tsx